import type {
  PlatformHealthOverallStatus,
  PlatformHealthRow,
  PlatformHealthSummary,
} from '../types/operations.types';

export function deriveOverallStatus(
  failureCount: number,
  warningCount: number
): PlatformHealthOverallStatus {
  if (failureCount > 0) {
    return 'investigate';
  }
  if (warningCount > 0) {
    return 'review';
  }
  return 'healthy';
}

function worstStatus(
  left: PlatformHealthOverallStatus,
  right: PlatformHealthOverallStatus
): PlatformHealthOverallStatus {
  const priority: Record<PlatformHealthOverallStatus, number> = {
    healthy: 0,
    review: 1,
    investigate: 2,
  };
  return priority[left] >= priority[right] ? left : right;
}

export function mergePlatformHealthSummaries(
  summaries: PlatformHealthSummary[],
  selectedDate?: string
): PlatformHealthRow[] {
  const byPlatform = new Map<
  string,
  Partial<Record<'reconciliation' | 'anomaly', PlatformHealthSummary>>
  >();

  for (const summary of summaries) {
    const existing = byPlatform.get(summary.platform) ?? {};
    existing[summary.source] = summary;
    byPlatform.set(summary.platform, existing);
  }

  return [...byPlatform.entries()]
    .map(([platform, sources]) => {
      const reconciliation = sources.reconciliation;
      const anomaly = sources.anomaly;
      const reconciliationStatus = reconciliation
        ? deriveOverallStatus(reconciliation.failure_count, reconciliation.warning_count)
        : 'healthy';
      const anomalyStatus = anomaly
        ? deriveOverallStatus(anomaly.failure_count, anomaly.warning_count)
        : 'healthy';

      const date = selectedDate
        ?? reconciliation?.latest_check
        ?? anomaly?.latest_check
        ?? '';

      return {
        platform,
        date,
        reconciliation_status: reconciliationStatus,
        anomaly_status: anomalyStatus,
        overall_status: worstStatus(reconciliationStatus, anomalyStatus),
      };
    })
    .sort((left, right) => left.platform.localeCompare(right.platform));
}

export const OVERALL_STATUS_LABELS: Record<PlatformHealthOverallStatus, string> = {
  healthy: 'Healthy',
  review: 'Review',
  investigate: 'Investigate',
};

export const OVERALL_STATUS_EMOJI: Record<PlatformHealthOverallStatus, string> = {
  healthy: '🟢',
  review: '🟡',
  investigate: '🔴',
};
